import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { leadsPageTitle } from '../leads/leadsNav';

const KEY = 'leads-window';
const DEFAULT = { open: false, lastPath: '/leads/overview', returnPath: '/dashboard', mode: 'max' };

/** Ownership moved to Campaign Tracker — never resume that old Leads URL. */
function leadsResumePath(path) {
  const raw = String(path || '').trim();
  if (!raw || raw.includes('campaign-ownership') || !raw.startsWith('/leads')) {
    return '/leads/overview';
  }
  return raw;
}

function load() {
  try {
    const stored = { ...DEFAULT, ...JSON.parse(localStorage.getItem(KEY) || '{}') };
    return { ...stored, lastPath: leadsResumePath(stored.lastPath) };
  } catch {
    return { ...DEFAULT };
  }
}

const LeadsWindowContext = createContext(null);

export function LeadsWindowProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState(load);
  const prevPath = useRef(location.pathname);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    const full = location.pathname + location.search;
    const onLeads = location.pathname.startsWith('/leads');
    setState((s) => {
      if (!onLeads) return s;
      const cameFromMain = !prevPath.current.startsWith('/leads');
      return {
        ...s,
        open: true,
        lastPath: full,
        returnPath: cameFromMain ? prevPath.current : s.returnPath,
      };
    });
    prevPath.current = location.pathname;
  }, [location]);

  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* ignore */ }
  }, [state]);

  const minimize = useCallback(() => {
    const full = location.pathname + location.search;
    const returnPath = stateRef.current.returnPath || '/dashboard';
    if (location.pathname.startsWith('/leads')) {
      setState((s) => ({ ...s, open: true, lastPath: full }));
    }
    navigate(returnPath);
  }, [location, navigate]);

  const close = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
    navigate(stateRef.current.returnPath || '/dashboard');
  }, [navigate]);

  const restore = useCallback(() => {
    navigate(leadsResumePath(stateRef.current.lastPath));
  }, [navigate]);

  const openLeads = useCallback(() => {
    const path = leadsResumePath(stateRef.current.lastPath);
    setState((s) => ({ ...s, open: true, lastPath: path }));
    navigate(path);
  }, [navigate]);

  const toggleMode = useCallback(() => {
    setState((s) => ({ ...s, mode: s.mode === 'max' ? 'window' : 'max' }));
  }, []);

  const value = {
    ...state,
    stepLabel: leadsPageTitle(state.lastPath?.split('?')[0] || ''),
    onLeads: location.pathname.startsWith('/leads'),
    minimize,
    close,
    restore,
    openLeads,
    toggleMode,
  };

  return <LeadsWindowContext.Provider value={value}>{children}</LeadsWindowContext.Provider>;
}

export function useLeadsWindow() {
  return useContext(LeadsWindowContext) || {
    open: false,
    onLeads: false,
    mode: 'max',
    lastPath: '/leads/overview',
    stepLabel: 'Leads Tracker',
    minimize() {},
    close() {},
    restore() {},
    openLeads() {},
    toggleMode() {},
  };
}
